import type { MessageApiInjection } from 'naive-ui/es/message/src/MessageProvider'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { type UiToastRequest } from '@sdk/api/modules/ui/types.ui'
import type { MessageOptions } from 'naive-ui/es/message/src/types'
import { useMessage } from 'naive-ui'

const messageProvider: Ref<MessageApiInjection | null> = ref(null)

const defaultToastOptions: MessageOptions = {
  closable: true,
  duration: 3500,
  keepAliveOnHover: true,
  showIcon: true,
}

export default function useToast() {
  return {
    initMessageProvider: () => {
      messageProvider.value = useMessage()
    },
    getMessageProvider: () => messageProvider.value,
    toast: Object.assign(
      (data: UiToastRequest) => {
        const message = messageProvider.value

        if (!message) {
          console.warn('Message provider not found.')
          return
        }

        const toastOptions = (Object.keys(defaultToastOptions) as (keyof MessageOptions)[]).reduce((acc, key) => {
          acc[key] = (data as any)[key] ?? defaultToastOptions[key]
          return acc
        }, {} as MessageOptions)

        switch (data.type) {
          case 'info':
          case 'error':
          case 'warning':
          case 'success':
          case 'loading':
            message[data.type](data.message, toastOptions)
            break
          default:
            console.warn(`Unsupported toast type: ${data.type}`)
        }
      },
      {
        info: (message: string, options?: MessageOptions) =>
          messageProvider.value?.info(message, { ...defaultToastOptions, ...options }),
        error: (message: string, options?: MessageOptions) =>
          messageProvider.value?.error(message, { ...defaultToastOptions, ...options }),
        warning: (message: string, options?: MessageOptions) =>
          messageProvider.value?.warning(message, { ...defaultToastOptions, ...options }),
        success: (message: string, options?: MessageOptions) =>
          messageProvider.value?.success(message, { ...defaultToastOptions, ...options }),
        loading: (message: string, options?: MessageOptions) =>
          messageProvider.value?.loading(message, { ...defaultToastOptions, ...options }),
      },
    ),
  }
}
